import { ReactElement, useCallback, useMemo } from 'react';
import { Stack, Typography, Box } from '@mui/material';
import { compact } from 'lodash';

import { Nullable } from '~globals/types/commons';
import { formatterDate } from '~utils/formatter';
import OrderActiveFilters from '~components/OrderActiveFilters';
import { OrderActiveFiltersItem } from '~components/OrderActiveFilters/types';

// eslint-disable-next-line max-len
import { useDashboardVariantV2GiverFinalizedFiltersContext } from '../../../../../FinalizedFiltersContext';
import { useDashboardVariantV2GiverContentFinalizedTabContext } from '../../Context';

import { DashboardVariantV2GiverContentActionsContainer } from '../../../../styles';

const DashboardVariantV2GiverContentFinalizedTabCurrentFilters =
  (): Nullable<ReactElement> => {
    const {
      autocompletesRefs: {
        itemState: itemStateAutocompleteRef,
        sourceImports: sourceImportsAutocompleteRef,
      },
      filters: {
        filtersActives,
        handleClearFilters,
        handleClearFilterByKey,
        handleClearFilterByValues,
      },
    } = useDashboardVariantV2GiverFinalizedFiltersContext();

    const {
      actions: { handleForceRequests },
    } = useDashboardVariantV2GiverContentFinalizedTabContext();

    const onClearFilter = useCallback(() => {
      handleClearFilters(true);
    }, [handleClearFilters]);

    const activeFilters = useMemo<OrderActiveFiltersItem[]>(() => {
      const currentFilters: OrderActiveFiltersItem[] = [];

      if (filtersActives.search) {
        currentFilters.push({
          id: 'search',
          label: filtersActives.search,
          tooltip: 'Nro. Orden / Nro. Tracking / Grupo',
          onClear: () =>
            handleClearFilterByKey('search', '', handleForceRequests),
        });
      }

      const rangeFinalizedDates = compact([
        filtersActives.finalizedDateTimeFrom,
        filtersActives.finalizedDateTimeTo,
      ]);

      if (rangeFinalizedDates.length > 0) {
        const rangeFinalizedDatesLabel = rangeFinalizedDates
          .map((date) =>
            formatterDate(date, {
              format: 'DD/MM/YYYY',
            }),
          )
          .join(' - ');

        currentFilters.push({
          id: 'rangeFinalizedDates',
          label: rangeFinalizedDatesLabel,
          tooltip: 'Rango de fechas de finalización',
          onClear: () =>
            handleClearFilterByValues(
              { finalizedDateTimeFrom: null, finalizedDateTimeTo: null },
              handleForceRequests,
            ),
        });
      }

      const rangeExpirationDates = compact([
        filtersActives.expirationDateTimeFrom,
        filtersActives.expirationDateTimeTo,
      ]);

      if (rangeExpirationDates.length > 0) {
        const rangeExpirationDatesLabel = rangeExpirationDates
          .map((date) =>
            formatterDate(date, {
              format: 'DD/MM/YYYY',
            }),
          )
          .join(' - ');

        currentFilters.push({
          id: 'rangeExpirationDates',
          label: rangeExpirationDatesLabel,
          tooltip: 'Rango de fechas de vencimiento',
          onClear: () =>
            handleClearFilterByValues(
              { expirationDateTimeFrom: null, expirationDateTimeTo: null },
              handleForceRequests,
            ),
        });
      }

      if (filtersActives.includeWithoutExpirationDate) {
        currentFilters.push({
          id: 'includeWithoutExpirationDate',
          label: 'Sin fecha de vencimiento',
          tooltip: 'Incluir pedidos sin fecha de vencimiento',
          onClear: () =>
            handleClearFilterByKey(
              'includeWithoutExpirationDate',
              null,
              handleForceRequests,
            ),
        });
      }

      if (
        filtersActives.itemState !== null &&
        filtersActives.itemState.length > 0
      ) {
        const itemStateLabels = filtersActives.itemState
          .map((service) => service.label.toString())
          .join(' / ');

        currentFilters.push({
          id: 'itemState',
          label: itemStateLabels,
          tooltip: 'Estados',
          onClear: () => {
            itemStateAutocompleteRef.current?.resetAutocomplete();
            handleClearFilterByKey('itemState', null, handleForceRequests);
          },
        });
      }

      if (
        filtersActives.sourceImports !== null &&
        filtersActives.sourceImports.length > 0
      ) {
        const sourceImportsCount = filtersActives.sourceImports.length - 1;

        const sourceImportsLabel = filtersActives.sourceImports.map(
          (sourceImport, sourceImportIndex) => (
            <Stack
              key={`filters-active-source-import-${sourceImport.value}`}
              direction="row"
              spacing={0.5}
              alignItems="center"
            >
              <Box
                component="img"
                src={sourceImport.image.url}
                alt={sourceImport.image.alt}
                sx={{ height: 15, width: 15, objectFit: 'contain' }}
              />

              <Typography variant="inherit">{sourceImport.label}</Typography>

              {sourceImportIndex < sourceImportsCount && (
                <Typography variant="inherit">/</Typography>
              )}
            </Stack>
          ),
        );

        currentFilters.push({
          id: 'sourceImports',
          label: sourceImportsLabel,
          tooltip: 'Medios de importación',
          onClear: () => {
            sourceImportsAutocompleteRef.current?.resetAutocomplete();
            handleClearFilterByKey('sourceImports', null, handleForceRequests);
          },
        });
      }

      return currentFilters;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      filtersActives,
      handleClearFilterByKey,
      handleClearFilterByValues,
      handleForceRequests,
    ]);

    if (activeFilters.length === 0) return null;

    return (
      <DashboardVariantV2GiverContentActionsContainer>
        <OrderActiveFilters
          filters={activeFilters}
          onClearFilters={onClearFilter}
        />
      </DashboardVariantV2GiverContentActionsContainer>
    );
  };

export default DashboardVariantV2GiverContentFinalizedTabCurrentFilters;
